import { ArrowDown, ArrowUp, Plus, Trash2 } from 'lucide-react';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { TagInput } from '@nucleus-commerce/admin-kit/forms';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import { Input } from '@nucleus-commerce/ui/components/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@nucleus-commerce/ui/components/select';
import { Switch } from '@nucleus-commerce/ui/components/switch';

import { fieldKeyFromLabel } from '../api/mappers';
import {
  FIELD_KINDS,
  MAX_FORM_FIELDS,
  type FormListName,
  type FormsForm,
} from '../schemas/forms-schema';

function FieldRow({
  list,
  index,
  count,
  disabled,
  onMove,
  onRemove,
}: {
  list: FormListName;
  index: number;
  count: number;
  disabled: boolean;
  onMove: (from: number, to: number) => void;
  onRemove: (index: number) => void;
}) {
  const { t } = useTranslation('chatSettings');
  const { control, getValues, setValue } = useFormContext<FormsForm>();
  const kind = useWatch({ control, name: `${list}.fields.${index}.kind` });
  const label = useWatch({ control, name: `${list}.fields.${index}.label` });
  const name = label.trim() || t('forms.untitled', { n: index + 1 });

  return (
    <li
      aria-label={name}
      className="flex flex-col gap-3 rounded-md border p-3"
      data-testid={`${list}-field-${index}`}
    >
      <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_10rem]">
        <FormField
          control={control}
          name={`${list}.fields.${index}.label`}
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('forms.label')}</FormLabel>
              <FormControl>
                <Input
                  disabled={disabled}
                  {...field}
                  onBlur={() => {
                    field.onBlur();
                    const keyPath = `${list}.fields.${index}.key` as const;
                    if (!getValues(keyPath)) {
                      setValue(keyPath, fieldKeyFromLabel(field.value), { shouldDirty: true });
                    }
                  }}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={control}
          name={`${list}.fields.${index}.key`}
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('forms.key')}</FormLabel>
              <FormControl>
                <Input className="font-mono" disabled={disabled} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={control}
          name={`${list}.fields.${index}.kind`}
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('forms.kind')}</FormLabel>
              <Select value={field.value} onValueChange={field.onChange} disabled={disabled}>
                <FormControl>
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {FIELD_KINDS.map((value) => (
                    <SelectItem key={value} value={value}>
                      {t(`forms.kinds.${value}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
      {kind === 'select' ? (
        <FormField
          control={control}
          name={`${list}.fields.${index}.options`}
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('forms.options')}</FormLabel>
              <FormControl>
                <TagInput
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  max={50}
                  maxLength={120}
                  disabled={disabled}
                  placeholder={t('forms.optionsPlaceholder')}
                  removeLabel={(option) => t('forms.removeOption', { option })}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      ) : null}
      {kind === 'department' || kind === 'file' ? (
        <p className="text-muted-foreground text-sm">{t(`forms.kindHint.${kind}`)}</p>
      ) : null}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <FormField
          control={control}
          name={`${list}.fields.${index}.required`}
          render={({ field }) => (
            <FormItem className="flex flex-row items-center gap-2">
              <FormControl>
                <Switch
                  checked={field.value}
                  onCheckedChange={field.onChange}
                  disabled={disabled}
                />
              </FormControl>
              <FormLabel className="font-normal">{t('forms.required')}</FormLabel>
            </FormItem>
          )}
        />
        {disabled ? null : (
          <div className="flex gap-1">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              aria-label={t('forms.moveUp', { name })}
              disabled={index === 0}
              onClick={() => onMove(index, index - 1)}
            >
              <ArrowUp aria-hidden />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              aria-label={t('forms.moveDown', { name })}
              disabled={index === count - 1}
              onClick={() => onMove(index, index + 1)}
            >
              <ArrowDown aria-hidden />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              aria-label={t('forms.remove', { name })}
              onClick={() => onRemove(index)}
            >
              <Trash2 aria-hidden />
            </Button>
          </div>
        )}
      </div>
    </li>
  );
}

/** Ordered list of up to ten fields of one widget form: label, key, kind, options, required. */
export function FormFieldsBuilder({ list, disabled }: { list: FormListName; disabled: boolean }) {
  const { t } = useTranslation('chatSettings');
  const { control } = useFormContext<FormsForm>();
  const { fields, append, remove, move } = useFieldArray({ control, name: `${list}.fields` });

  return (
    <div className="flex flex-col gap-3">
      {fields.length === 0 ? (
        <p className="text-muted-foreground text-sm">{t('forms.noFields')}</p>
      ) : (
        <ol className="flex flex-col gap-3" aria-label={t(`forms.${list}.fieldsLabel`)}>
          {fields.map((field, index) => (
            <FieldRow
              key={field.id}
              list={list}
              index={index}
              count={fields.length}
              disabled={disabled}
              onMove={move}
              onRemove={remove}
            />
          ))}
        </ol>
      )}
      {disabled ? null : (
        <div className="flex flex-wrap items-center gap-3">
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={fields.length >= MAX_FORM_FIELDS}
            onClick={() =>
              append({ key: '', kind: 'text', label: '', required: false, options: [] })
            }
          >
            <Plus aria-hidden />
            {t(`forms.${list}.add`)}
          </Button>
          <p className="text-muted-foreground text-sm">
            {t('forms.limit', { count: fields.length, max: MAX_FORM_FIELDS })}
          </p>
        </div>
      )}
    </div>
  );
}
